import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImageUploader from '../../components/admin/ImageUploader';
import {
  deleteMember,
  fetchMemberById,
  getNextMemberSortOrder,
  upsertMember,
} from '../../lib/membersApi';

const emptyForm = {
  name: '',
  logo_url: '',
  external_url: '',
  sort_order: 1,
  is_published: true,
};

export default function MemberForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (isNew) {
      const nextOrder = await getNextMemberSortOrder();
      setForm((f) => ({ ...f, sort_order: nextOrder }));
      return;
    }

    setLoading(true);
    try {
      const member = await fetchMemberById(id);
      setForm({
        name: member.name,
        logo_url: member.logo_url || '',
        external_url: member.external_url || '',
        sort_order: member.sort_order,
        is_published: member.is_published,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        logo_url: form.logo_url.trim() || null,
        external_url: form.external_url.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        is_published: form.is_published,
      };
      if (!isNew) payload.id = id;
      await upsertMember(payload);
      navigate('/admin/members');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this member?')) return;
    await deleteMember(id);
    navigate('/admin/members');
  };

  const handleRemoveLogo = () => set('logo_url', '');

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <h1 className="admin-page-title">{isNew ? 'New member' : 'Edit member'}</h1>

      <form onSubmit={handleSave} className="admin-form-card">
        {error && <p className="admin-login-error">{error}</p>}

        <div className="admin-form-field">
          <label htmlFor="member-name">Name *</label>
          <input
            id="member-name"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        <div className="admin-form-field">
          <label htmlFor="member-url">Website URL</label>
          <input
            id="member-url"
            type="url"
            value={form.external_url}
            onChange={(e) => set('external_url', e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="admin-form-field">
          <label htmlFor="member-order">Sort order</label>
          <input
            id="member-order"
            type="number"
            min="0"
            value={form.sort_order}
            onChange={(e) => set('sort_order', e.target.value)}
          />
        </div>

        <div className="admin-form-field">
          <label htmlFor="member-published">Status</label>
          <select
            id="member-published"
            value={form.is_published ? 'published' : 'hidden'}
            onChange={(e) => set('is_published', e.target.value === 'published')}
          >
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>

        <ImageUploader
          bucket="member-logos"
          pathPrefix="logos"
          currentUrl={form.logo_url || undefined}
          onUpload={(url) => set('logo_url', url)}
          label="Logo (optional)"
        />

        {form.logo_url && (
          <button type="button" onClick={handleRemoveLogo} className="admin-link admin-link--danger">
            Remove logo
          </button>
        )}

        <div className="admin-form-actions">
          <button type="submit" disabled={saving} className="admin-btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => navigate('/admin/members')} className="admin-btn-secondary">
            Cancel
          </button>
          {!isNew && (
            <button type="button" onClick={handleDelete} className="admin-link admin-link--danger admin-form-actions__delete">
              Delete member
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
